import { useEffect, useMemo, useRef, useState } from 'react';
import {
  aggregatePsdSeries,
  getCachedSegmentPsd,
} from '../../../shared/spectral';
import type { SegmentPsdResult } from '../../../shared/spectral';
import type { PsdSeries } from '../../../types';
import type {
  ChartViewId,
  CustomTimeSegment,
  SpectralWorkspaceSession,
} from '../types';
import {
  buildTrendEndOptions,
  buildTrendStartOptions,
} from '../utils/trendZoom';
import {
  filterSessionEventsForSegment,
  getWorkspaceAvailableSeconds,
  resolveSegmentBounds,
} from '../utils/resolveSpectraSegment';

type UseSpectraChartDataParams = {
  readySessions: SpectralWorkspaceSession[];
  activeViewId: ChartViewId;
  customSegments: CustomTimeSegment[];
  trendStart: string | null;
  trendEnd: string | null;
  selectedSessionId: string | null;
  logScale: boolean;
  spectraStart: string | null;
  spectraEnd: string | null;
  computeEnabled?: boolean;
};

export type SpectraExcludedSession = {
  sessionId: number;
  reason: string;
};

export type SpectraChartPoint = {
  frequency: number;
  power: number;
};

type SessionPsdEntry = {
  sessionId: number;
  result: SegmentPsdResult;
};

const formatFrequencyLabel = (frequency: number) =>
  `${frequency.toFixed(3)} Hz`;

const buildSessionFingerprints = (sessions: SpectralWorkspaceSession[]) =>
  sessions
    .map(session => `${session.sessionId}:${session.events?.length ?? 0}`)
    .join('|');

const computePerSessionResults = ({
  sessions,
  segmentBounds,
  selectedSessionId,
}: {
  sessions: SpectralWorkspaceSession[];
  segmentBounds: { startSecond: number; endSecond: number };
  selectedSessionId: string | null;
}): SessionPsdEntry[] => {
  const sessionsToCompute = selectedSessionId
    ? sessions.filter(
        session => session.sessionId === Number(selectedSessionId)
      )
    : sessions;

  return sessionsToCompute.map(session => {
    const segmentEvents = filterSessionEventsForSegment(
      session.events ?? [],
      segmentBounds.startSecond,
      segmentBounds.endSecond
    );

    return {
      sessionId: session.sessionId,
      result: getCachedSegmentPsd(
        session.sessionId,
        segmentBounds.startSecond,
        segmentBounds.endSecond,
        segmentEvents
      ),
    };
  });
};

const transformPsdToChartData = (
  psd: PsdSeries,
  logScale: boolean
): SpectraChartPoint[] =>
  psd.frequencies.map((frequency, index) => ({
    frequency,
    power: logScale ? Math.log10(psd.psd_lf[index] + 1e-6) : psd.psd_lf[index],
  }));

const filterChartDataByFrequency = (
  points: SpectraChartPoint[],
  spectraStart: string | null,
  spectraEnd: string | null
) => {
  if (!spectraStart && !spectraEnd) return points;

  const startFrequency = spectraStart ? Number(spectraStart) : -Infinity;
  const endFrequency = spectraEnd ? Number(spectraEnd) : Infinity;

  return points.filter(
    point =>
      point.frequency >= startFrequency && point.frequency <= endFrequency
  );
};

const resolveSelectedPsd = ({
  perSessionResults,
  selectedSessionId,
  segmentBounds,
}: {
  perSessionResults: SessionPsdEntry[];
  selectedSessionId: string | null;
  segmentBounds: { startSecond: number; endSecond: number } | null;
}): PsdSeries | null => {
  if (!segmentBounds) return null;

  if (!selectedSessionId) {
    const allValid = perSessionResults
      .map(entry => (entry.result.ok ? entry.result.psd : null))
      .filter((psd): psd is PsdSeries => psd != null);

    return allValid.length > 0 ? aggregatePsdSeries(allValid) : null;
  }

  const sessionId = Number(selectedSessionId);
  const entry = perSessionResults.find(item => item.sessionId === sessionId);
  if (!entry?.result.ok) return null;
  return entry.result.psd;
};

export const useSpectraChartData = ({
  readySessions,
  activeViewId,
  customSegments,
  trendStart,
  trendEnd,
  selectedSessionId,
  logScale,
  spectraStart,
  spectraEnd,
  computeEnabled = true,
}: UseSpectraChartDataParams) => {
  const readySessionsRef = useRef(readySessions);
  readySessionsRef.current = readySessions;

  const [perSessionResults, setPerSessionResults] = useState<SessionPsdEntry[]>(
    []
  );
  const [isSpectraComputing, setIsSpectraComputing] = useState(false);

  const sessionFingerprints = useMemo(
    () => buildSessionFingerprints(readySessions),
    [readySessions]
  );

  const availableSeconds = useMemo(
    () => getWorkspaceAvailableSeconds(readySessions),
    [sessionFingerprints]
  );

  const maxAvailableSecond = useMemo(
    () =>
      availableSeconds.length > 0
        ? availableSeconds[availableSeconds.length - 1]
        : 0,
    [availableSeconds]
  );

  const trendStartOptions = useMemo(
    () => buildTrendStartOptions(availableSeconds),
    [availableSeconds]
  );

  const trendEndOptions = useMemo(
    () => buildTrendEndOptions(availableSeconds, trendStart),
    [availableSeconds, trendStart]
  );

  const segmentBounds = useMemo(
    () =>
      resolveSegmentBounds({
        activeViewId,
        customSegments,
        availableSeconds,
        trendStart,
        trendEnd,
      }),
    [activeViewId, availableSeconds, customSegments, trendEnd, trendStart]
  );

  const segmentKey = segmentBounds
    ? `${segmentBounds.startSecond}-${segmentBounds.endSecond}`
    : null;

  const computeKey = `${segmentKey ?? 'none'}|${sessionFingerprints}|${selectedSessionId ?? 'group'}`;

  useEffect(() => {
    if (!computeEnabled || !segmentBounds) {
      if (!computeEnabled) {
        setIsSpectraComputing(false);
      } else {
        setPerSessionResults([]);
        setIsSpectraComputing(false);
      }
      return;
    }

    setIsSpectraComputing(true);
    const timeoutId = window.setTimeout(() => {
      const results = computePerSessionResults({
        sessions: readySessionsRef.current,
        segmentBounds,
        selectedSessionId,
      });
      setPerSessionResults(results);
      setIsSpectraComputing(false);
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [computeEnabled, computeKey, segmentBounds, selectedSessionId]);

  const sessionSelectData = useMemo(
    () =>
      readySessionsRef.current.map(session => ({
        value: session.sessionId.toString(),
        label: `${session.sessionId} · ${session.patientCode ?? 'n/a'}`,
      })),
    [sessionFingerprints]
  );

  const excludedSessions = useMemo<SpectraExcludedSession[]>(
    () =>
      perSessionResults
        .filter(entry => !entry.result.ok)
        .map(entry => ({
          sessionId: entry.sessionId,
          reason: entry.result.ok ? '' : entry.result.reason,
        })),
    [perSessionResults]
  );

  const selectedPsd = useMemo(
    () =>
      resolveSelectedPsd({
        perSessionResults,
        selectedSessionId,
        segmentBounds,
      }),
    [perSessionResults, segmentBounds, selectedSessionId]
  );

  const rawChartData = useMemo(
    () => (selectedPsd ? transformPsdToChartData(selectedPsd, logScale) : []),
    [logScale, selectedPsd]
  );

  const psdChartData = useMemo(
    () => filterChartDataByFrequency(rawChartData, spectraStart, spectraEnd),
    [rawChartData, spectraEnd, spectraStart]
  );

  const availableFrequencies = useMemo(
    () => rawChartData.map(point => point.frequency),
    [rawChartData]
  );

  const spectraStartOptions = useMemo(() => {
    if (availableFrequencies.length < 2) return [];
    return availableFrequencies.slice(0, -1).map(frequency => ({
      value: frequency.toString(),
      label: formatFrequencyLabel(frequency),
    }));
  }, [availableFrequencies]);

  const spectraEndOptions = useMemo(() => {
    if (availableFrequencies.length < 2) return [];
    const startFrequency = spectraStart ? Number(spectraStart) : null;
    const candidates =
      startFrequency == null
        ? availableFrequencies.slice(1)
        : availableFrequencies.filter(frequency => frequency > startFrequency);

    return candidates.map(frequency => ({
      value: frequency.toString(),
      label: formatFrequencyLabel(frequency),
    }));
  }, [availableFrequencies, spectraStart]);

  const includedSessionCount = useMemo(() => {
    if (!segmentBounds) return 0;

    if (perSessionResults.length > 0) {
      return perSessionResults.filter(entry => entry.result.ok).length;
    }

    return 0;
  }, [perSessionResults, segmentBounds]);

  return {
    availableSeconds,
    maxAvailableSecond,
    trendStartOptions,
    trendEndOptions,
    segmentBounds,
    sessionSelectData,
    excludedSessions,
    psdChartData,
    spectraStartOptions,
    spectraEndOptions,
    availableFrequencies,
    includedSessionCount,
    isSpectraComputing,
  };
};
